import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
  DocumentReference,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { UseFormSetValue } from "react-hook-form";
import { AnySchema } from "yup";

import { db, draftFieldConverter } from "@api";
import { LockedField, LockedFields, useDraftStore } from "@store/draft";
import { useUserStore } from "@store/user";

type useDraftProps = {
  formId: string;
  setValue: UseFormSetValue<{ [x: string]: AnySchema }>;
};

export const useDraft = ({
  formId,
  setValue,
}: useDraftProps): { activeUsers: LockedField[] } => {
  const focusFieldId = useDraftStore((state) => state.focusFieldId);
  const { setLockedFields } = useDraftStore();
  const { user } = useUserStore();
  const [activeUsers, setActiveUsers] = useState<LockedField[]>([]);

  useEffect(() => {
    let reference: DocumentReference | null = null;

    if (user && formId) {
      reference = doc(db, "formInstances", formId, "activeUsers", user.id);

      setDoc(reference, {
        name: `${user.firstName} ${user.lastName} `,
        createdAt: new Date(),
      });
    }

    return () => {
      if (user && reference) {
        deleteDoc(reference);
      }
    };
  }, [user]);

  useEffect(() => {
    if (formId) {
      const unsubscribe = onSnapshot(
        collection(db, "formInstances", formId, "fields").withConverter(
          draftFieldConverter,
        ),
        (snapshot) => {
          const _lockedFields: LockedFields = {};

          for (const doc of snapshot.docs) {
            if (doc.data().isLocked) {
              _lockedFields[doc.id] = {
                lockedByUserName: doc.data().lockedByUserName,
                lockedByUserId: doc.data().lockedByUserId,
                lastTouchedAt: doc.data().lastTouchedAt.toDate(),
              };
            }
          }

          setLockedFields(_lockedFields);

          for (const change of snapshot.docChanges()) {
            if (
              change.doc.data().value &&
              focusFieldId !== change.doc.id &&
              setValue
            ) {
              setValue(
                change.doc.id,
                change.doc.data().value as unknown as AnySchema,
              );
            }
          }
        },
      );

      return () => unsubscribe();
    }
  }, [formId, focusFieldId]);

  useEffect(() => {
    if (formId) {
      const unsubscribe = onSnapshot(
        collection(db, "formInstances", formId, "activeUsers"),
        (snapshot) => {
          const _activeUsers: LockedField[] = [];

          for (const doc of snapshot.docs) {
            _activeUsers.push({
              lockedByUserName: doc.data().name,
            });
          }

          setActiveUsers(_activeUsers);
        },
      );

      return () => unsubscribe();
    }
  }, [formId, focusFieldId]);

  return {
    activeUsers,
  };
};
